import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import NoData from '../../../components/NoData';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { Typography, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SelectDropdown from '../../../components/SelectDropdown';
import { usePercOverTimeDataHooks } from '../hooks/usePercOverTimeDataHooks';
import { OPPS_DATA } from '../constants';
import { legendHighlightText } from '../service/SpendAndOppurtunitiesService';
import { chartStyles } from '../styles/spendAndOppsStyles';

const PercOverTimeLineGraph = ({
  selectedStore,
  currentYear,
  totalYears,
  userStores,
  dataType,
  spendToggleState,
}) => {
  const [viewType, setViewType] = useState('Weekly');
  const {
    selectedYears,
    isLoading,
    error,
    percOverTimeData,
    handleYearChange,
    handleOOTStoreChange,
    selectedComparisonStore,
    compareWith,
  } = usePercOverTimeDataHooks(
    selectedStore,
    currentYear,
    viewType,
    userStores,
    dataType,
    spendToggleState
  );

  const yearsList = Array.from({ length: totalYears }, (_, i) => ({
    label: `${currentYear - i}`,
    value: currentYear - i,
  }));

  const handleViewTypeChange = (event, newViewType) => {
    if (newViewType) {
      setViewType(newViewType);
    }
  };

  const handleYearsChange = (event) => {
    handleYearChange(event);
  };

  const handleComparisonStoreChange = (newComparisonStore) => {
    handleOOTStoreChange(newComparisonStore);
  };

  return (
    <div id="PercOverTimeContainer" style={{ height: '550px', width: '100%', margin: 'auto' }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: '500', paddingBottom: '20px', paddingTop: '25px' }}
      >
        {dataType === OPPS_DATA ? 'Opportunities' : 'Spends'} % to Plan Over Time
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ display: 'flex', marginRight: 'auto' }}>
          <div>
            <Typography variant="body2">Years</Typography>
            <SelectDropdown
              isSelectAll={true}
              multiple={true}
              multiSelectDefaultValues={[yearsList[0]?.value]}
              options={yearsList}
              handleFilterChange={handleYearsChange}
              value={selectedYears}
              disabled={isLoading}
            />
          </div>
          <div>
            <Typography variant="body2">Compare With</Typography>
            <SelectDropdown
              options={compareWith}
              value={selectedComparisonStore}
              handleFilterChange={handleComparisonStoreChange}
              disabled={isLoading}
            />
          </div>
        </Box>
        <div>
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={handleViewTypeChange}
            aria-label="toggle view"
            disabled={isLoading}
          >
            <ToggleButton
              value={'Weekly'}
              style={{
                background: viewType === 'Weekly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
                color: viewType === 'Weekly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
                borderColor: isLoading ? '#a9a9ab' : 'black',
                textTransform: 'capitalize',
                height: '40px',
              }}
            >
              Weekly
            </ToggleButton>
            <ToggleButton
              value={'Monthly'}
              style={{
                background: viewType === 'Monthly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
                color: viewType === 'Monthly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
                borderColor: isLoading ? '#a9a9ab' : 'black',
                textTransform: 'capitalize',
                height: '40px',
              }}
            >
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Box>

      {error ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
        >
          <NoData textValue="Error loading data" />
        </Box>
      ) : isLoading ? (
        <ShimmerComponent
          updatedStyle={{
            width: '100%',
            height: '70%',
            backgroundSize: '2000px 800px',
            border: '0px',
          }}
        >
          <BouncingLogo height="48vh" text="Loading..." />
        </ShimmerComponent>
      ) : percOverTimeData && percOverTimeData.series.length > 0 ? (
        <>
          <ReactEcharts
            style={{ height: '400px', width: '100%' }}
            option={{
              xAxis: {
                name: 'Time',
                type: 'category',
                data: percOverTimeData.xAxisData,
                axisLabel: {
                  ...chartStyles.axisLabel,
                  interval: 0,
                  rotate: 45,
                },
                nameTextStyle: chartStyles.axisNameText,
                axisLine: {
                  lineStyle: chartStyles.axisLine,
                },
                axisTick: {
                  show: true,
                  lineStyle: chartStyles.axisLine,
                },
              },
              yAxis: {
                type: 'value',
                name: dataType === OPPS_DATA ? 'Opportunities (%)' : 'Spends (%)',
                axisLabel: {
                  ...chartStyles.axisLabel,
                  formatter: '{value}',
                },
                nameTextStyle: chartStyles.axisNameText,
                axisTick: {
                  show: true,
                  lineStyle: chartStyles.axisLine,
                },
                axisLine: {
                  lineStyle: chartStyles.axisLine,
                },
              },
              tooltip: { trigger: 'axis' },
              legend: {
                data: percOverTimeData.legend,
                bottom: 0,
                type: 'scroll',
                textStyle: chartStyles.legendText,
              },
              series: [...percOverTimeData.series],
            }}
            opts={{
              renderer: 'canvas',
              devicePixelRatio: window.devicePixelRatio || 2,
            }}
          />
          {legendHighlightText()}
        </>
      ) : (
        <NoData />
      )}
    </div>
  );
};

PercOverTimeLineGraph.propTypes = {
  selectedStore: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)])
    .isRequired,
  currentYear: PropTypes.number.isRequired,
  totalYears: PropTypes.number,
  userStores: PropTypes.arrayOf(
    PropTypes.shape({
      branch_number: PropTypes.number,
      branch_name: PropTypes.string,
      region_id: PropTypes.number,
      region_name: PropTypes.string,
    })
  ).isRequired,
  dataType: PropTypes.string.isRequired,
  spendToggleState: PropTypes.bool,
};

PercOverTimeLineGraph.defaultProps = {
  totalYears: 0,
  spendToggleState: false,
};

export default PercOverTimeLineGraph;
